import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useData } from "@/context/data-provider";
import { SK } from "@/lib/constants";
import { downloadJson, toCsv, downloadCsv } from "@/lib/csv";
import { dbGet, dbSet, dbKeys, dbDelete } from "@/lib/db";
import { calcCompliance } from "@/lib/scoring";
import { PRIO_LABELS, POT_LABELS, APOIO_LABELS } from "@/lib/constants";

export default function Configuracoes() {
  const {
    doctors: { doctors, setAllDoctors },
    visits: { visits, setAllVisits },
    period: { period, history, setAllPeriod },
    products: { products, addProduct, removeProduct, setAllProducts },
    zones: { zones, addZone, removeZone, setAllZones },
    apoios: { apoios, setAllApoios },
    todos: { todos, setAllTodos },
  } = useData();

  const [newProduct, setNewProduct] = useState("");
  const [newZone, setNewZone] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [importFeedback, setImportFeedback] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [appVersion, setAppVersion] = useState("");

  useEffect(() => {
    fetch("/manifest.json").then((r) => r.json()).then((m) => setAppVersion(m.version ?? "")).catch(() => {});
  }, []);

  const handleAddProduct = () => { if (newProduct.trim()) { addProduct(newProduct.trim()); setNewProduct(""); } };
  const handleAddZone = () => { if (newZone.trim()) { addZone(newZone.trim()); setNewZone(""); } };

  const exportAll = async () => {
    // Collect all data including plan keys
    const allKeys = await dbKeys();
    const planKeys = allKeys.filter((k) => k.startsWith(SK.PLAN + "_"));
    const planData: Record<string, unknown> = {};
    for (const k of planKeys) {
      planData[k] = await dbGet(k);
    }

    downloadJson("medvisit_backup.json", {
      [SK.DOCTORS]: doctors,
      [SK.VISITS]: visits,
      [SK.PERIOD]: period,
      [SK.PERIOD_HISTORY]: history,
      [SK.PRODUCTS]: products,
      [SK.ZONES]: zones,
      [SK.APOIOS]: apoios,
      [SK.TODOS]: todos,
      _plans: planData,
    });
  };

  const exportDoctorsCsv = () => {
    const headers = ["Nome", "Local", "Especialidade", "Prioridade", "Potencial", "Telefone", "Email", "Morada", "Zona"];
    const rows = doctors.map((d) => [d.name, d.local, d.espec, PRIO_LABELS[d.prio], POT_LABELS[d.pot], d.tel, d.email, d.addr, d.zone]);
    downloadCsv("medicos.csv", toCsv(headers, rows));
  };

  const exportCoverageCsv = () => {
    const headers = ["Médico", "Local", "Prioridade", "Potencial", "Meta", "Visitas", "Estado"];
    const rows = doctors.map((d) => {
      const comp = calcCompliance(d, visits, period);
      return [
        d.name, d.local, PRIO_LABELS[d.prio], POT_LABELS[d.pot],
        String(comp.target), String(comp.actual),
        comp.status === "ok" ? "Cumprido" : comp.status === "partial" ? "Parcial" : "Sem visita",
      ];
    });
    downloadCsv(`cobertura_${period.start}_${period.end}.csv`, toCsv(headers, rows));
  };

  const exportSupportsCsv = () => {
    const headers = ["Médico", "Evento", "Tipo", "Início", "Fim", "Notas"];
    const rows = apoios.map((a) => [a.medicoNome, a.evento, APOIO_LABELS[a.tipo], a.dataInicio, a.dataFim, a.notas]);
    downloadCsv("apoios.csv", toCsv(headers, rows));
  };

  const importData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);

        // Support both formats:
        // 1. Original app format: { doctors, allVisits, activePeriod, periodHistory, products, zones, apoios, todos }
        // 2. React app format: { mv5_doctors, mv5_visits, mv5_period, mv5_ph, mv5_products, mv5_zones, mv5_apoios, mv5_todos, _plans }
        const d = data.doctors ?? data[SK.DOCTORS];
        const v = data.allVisits ?? data[SK.VISITS];
        const p = data.activePeriod ?? data[SK.PERIOD];
        const ph = data.periodHistory ?? data[SK.PERIOD_HISTORY];
        const pr = data.products ?? data[SK.PRODUCTS];
        const z = data.zones ?? data[SK.ZONES];
        const ap = data.apoios ?? data[SK.APOIOS];
        const td = data.todos ?? data[SK.TODOS];

        if (d) setAllDoctors(d);
        if (v) setAllVisits(v);
        if (p) setAllPeriod(p, ph ?? []);
        if (pr) setAllProducts(pr);
        if (z) setAllZones(z);
        if (ap) setAllApoios(ap);
        if (td) setAllTodos(td);

        // Import plan keys (React format)
        if (data._plans && typeof data._plans === "object") {
          for (const [key, value] of Object.entries(data._plans)) {
            await dbSet(key, value);
          }
        }
        // Also handle flat plan keys
        for (const [key, value] of Object.entries(data)) {
          if (key.startsWith(SK.PLAN + "_") || key.startsWith("mv5_plan")) {
            await dbSet(key, value);
          }
        }

        setImportFeedback("✓ Dados importados com sucesso!");
        setTimeout(() => setImportFeedback(""), 3000);
      } catch {
        setImportFeedback("✕ Ficheiro inválido");
        setTimeout(() => setImportFeedback(""), 3000);
      }
    };
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const clearAllData = async () => {
    setAllDoctors([]); setAllVisits({}); setAllProducts([]); setAllZones([]); setAllApoios([]); setAllTodos([]);
    // Also clear plan keys
    const allKeys = await dbKeys();
    for (const k of allKeys) {
      if (k.startsWith(SK.PLAN)) await dbDelete(k);
    }
    setConfirmClear(false);
  };

  return (
    <div className="container">
      <h1 className="font-serif text-[1.3rem] mb-4" style={{ color: "var(--mv-ink-dark)" }}>Configurações</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[18px]">
        {/* Products */}
        <div className="mv-panel">
          <div className="mv-panel-header">
            <span className="mv-panel-title">Produtos / Temas</span>
            <span className="mv-badge">{products.length}</span>
          </div>
          <div className="p-[14px_18px]">
            {products.map((p) => (
              <div key={p} className="flex items-center gap-[10px] py-2" style={{ borderBottom: "1px solid var(--mv-border)" }}>
                <span className="flex-1 text-[0.82rem]">{p}</span>
                <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={() => removeProduct(p)}>×</button>
              </div>
            ))}
            {products.length === 0 && <div className="text-[0.8rem] py-2" style={{ color: "var(--mv-ink-light)" }}>Nenhum produto</div>}
            <div className="flex gap-2 mt-3">
              <input className="mv-input flex-1" placeholder="Novo produto..." value={newProduct} onChange={(e) => setNewProduct(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddProduct()} />
              <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={handleAddProduct} disabled={!newProduct.trim()}>+</button>
            </div>
          </div>
        </div>

        {/* Zones */}
        <div className="mv-panel">
          <div className="mv-panel-header">
            <span className="mv-panel-title">Zonas Geográficas</span>
            <span className="mv-badge">{zones.length}</span>
          </div>
          <div className="p-[14px_18px]">
            {zones.map((z) => (
              <div key={z} className="flex items-center gap-[10px] py-2" style={{ borderBottom: "1px solid var(--mv-border)" }}>
                <span className="flex-1 text-[0.82rem]">{z}</span>
                <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={() => removeZone(z)}>×</button>
              </div>
            ))}
            {zones.length === 0 && <div className="text-[0.8rem] py-2" style={{ color: "var(--mv-ink-light)" }}>Nenhuma zona</div>}
            <div className="flex gap-2 mt-3">
              <input className="mv-input flex-1" placeholder="Nova zona..." value={newZone} onChange={(e) => setNewZone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddZone()} />
              <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={handleAddZone} disabled={!newZone.trim()}>+</button>
            </div>
          </div>
        </div>
      </div>

      {/* Data */}
      <div className="mv-panel mt-[18px] max-w-[640px]">
        <div className="mv-panel-header">
          <span className="mv-panel-title">Exportar</span>
        </div>
        <div className="p-[14px_18px] flex flex-col gap-2">
          <button className="mv-btn mv-btn-ghost w-full justify-start" onClick={exportAll}>📥 Exportar tudo (JSON)</button>
          <button className="mv-btn mv-btn-ghost w-full justify-start" onClick={exportDoctorsCsv}>📥 Exportar médicos (CSV)</button>
          <button className="mv-btn mv-btn-ghost w-full justify-start" onClick={exportCoverageCsv}>📥 Exportar cobertura (CSV)</button>
          <button className="mv-btn mv-btn-ghost w-full justify-start" onClick={exportSupportsCsv}>📥 Exportar apoios (CSV)</button>
        </div>
      </div>

      <div className="mv-panel mt-[18px] max-w-[640px]">
        <div className="mv-panel-header">
          <span className="mv-panel-title">Importar</span>
        </div>
        <div className="p-[14px_18px] flex flex-col gap-2">
          <button className="mv-btn mv-btn-ghost w-full justify-start" onClick={() => fileRef.current?.click()}>📤 Importar dados (JSON)</button>
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={importData} />
          {importFeedback && (
            <div className="text-[0.76rem] font-semibold" style={{ color: importFeedback.startsWith("✓") ? "var(--mv-green)" : "var(--mv-red)" }}>
              {importFeedback}
            </div>
          )}
        </div>
      </div>

      <div className="mv-panel mt-[18px] max-w-[640px]">
        <div className="mv-panel-header">
          <span className="mv-panel-title">Zona de Perigo</span>
        </div>
        <div className="p-[14px_18px]">
          <button className="mv-btn mv-btn-danger w-full justify-start" onClick={() => setConfirmClear(true)}>🗑 Limpar todos os dados</button>
        </div>
      </div>

      {/* About */}
      <div className="mv-panel mt-[18px] max-w-[640px]">
        <div className="mv-panel-header">
          <span className="mv-panel-title">ℹ️ Sobre</span>
        </div>
        <div className="p-[14px_18px] text-[0.8rem] leading-[1.6]" style={{ color: "var(--mv-ink-light)" }}>
          <strong style={{ color: "var(--mv-ink-dark)" }}>MedVisit Pro</strong>
          {appVersion && <span className="font-mono text-[0.62rem] ml-[6px]" style={{ color: "var(--mv-ink-xlight)" }}>v{appVersion}</span>}
          <br />
          Gestão de painel de clientes médicos<br />
          para Delegados de Informação Médica.
          <br /><br />
          Dados guardados localmente no dispositivo.<br />
          Use "Exportar" para fazer backup.
          <br /><br />
          © Bruno Conceição<br />
          <a href="mailto:conceiba@gmail.com" style={{ color: "var(--mv-blue)" }}>conceiba@gmail.com</a>
        </div>
      </div>

      {/* Confirm clear modal */}
      {confirmClear && createPortal(
        <div
          className="fixed inset-0 z-[600] flex items-center justify-center"
          style={{ background: "rgba(15,46,90,0.5)", backdropFilter: "blur(3px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmClear(false); }}
        >
          <div className="mx-4 w-full max-w-[400px] rounded-[16px]" style={{ background: "var(--mv-surface)", border: "1px solid var(--mv-border)", boxShadow: "0 20px 60px rgba(15,46,90,0.25)" }}>
            <div className="mv-panel-header justify-between">
              <span className="mv-panel-title">Confirmar Eliminação</span>
              <button className="text-[1.15rem] opacity-40 hover:opacity-100" onClick={() => setConfirmClear(false)}>✕</button>
            </div>
            <div className="p-5">
              <p className="text-[0.84rem] mb-4" style={{ color: "var(--mv-ink-light)" }}>
                Esta ação irá eliminar todos os dados permanentemente. Tem a certeza?
              </p>
              <div className="flex gap-2 justify-end">
                <button className="mv-btn mv-btn-ghost" onClick={() => setConfirmClear(false)}>Cancelar</button>
                <button className="mv-btn mv-btn-danger" onClick={clearAllData}>Eliminar Tudo</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
